import extractConfig from './utils/extractConfig';
import getDatapointStatus from './utils/getDatapointStatus';
import { ActionDatapoint } from './datapoints/ActionDatapoint';
import { ActionFunction, LegacyDispatchFunction } from 'satcheljs/lib/legacy';
import { ActionMessage, DispatchFunction } from 'satcheljs';
import { DatapointStatus } from './types/DatapointEnums';
import { safeRequestAnimationFrame } from 'owa-performance';
import { registerCreateServiceResponseCallback as registerOwsCreateServiceResponseCallback } from 'owa-service/lib/fetchWithRetry';
import { registerCreateServiceResponseCallback as registerOwsPrimeCreateServiceResponseCallback } from 'owa-ows-gateway/lib/registerCreateServiceResponseCallback';
import { hasQueryStringParameter } from 'owa-querystring';
import RequestOptions from 'owa-service/lib/RequestOptions';
import { DatapointConfig } from 'owa-analytics-actions';

let actionDatapointStack: ActionDatapoint[] = [];

export function clearActionDatapointStack() {
    actionDatapointStack = [];
}

export function returnTopExecutingActionDatapoint(
    filterFunction?: (dp: ActionDatapoint) => boolean
) {
    if (filterFunction) {
        return actionDatapointStack.filter(filterFunction)[0];
    }

    return actionDatapointStack[0];
}

function trackServiceResponses(
    responsePromise: Promise<Response>,
    actionName: string,
    url: string,
    attemptCount: number,
    options: RequestOptions
) {
    var datapoint = returnTopExecutingActionDatapoint();
    if (datapoint) {
        datapoint.trackNetworkResponse(responsePromise, actionName, options);
    }
}

registerOwsCreateServiceResponseCallback(trackServiceResponses);
registerOwsPrimeCreateServiceResponseCallback(trackServiceResponses);

export function addDatapointMiddleware(next: DispatchFunction, actionMessage: ActionMessage) {
    addDataPointInternal(
        actionMessage.type,
        next.bind(null, actionMessage),
        actionMessage.dp,
        actionMessage
    );
}

export function addDatapointLegacyMiddleware(
    next: LegacyDispatchFunction,
    action: ActionFunction,
    actionType: string,
    args: IArguments,
    options: any
) {
    return addDataPointInternal(
        actionType,
        next.bind(null, action, actionType, args, options),
        extractConfig(actionType, options?.datapoint, args)
    );
}

function addDataPointInternal(
    actionType: string,
    executeNext: () => any,
    config: DatapointConfig,
    actionMessage?: ActionMessage & { lazyOrchestrator?: boolean }
) {
    let datapoint: ActionDatapoint = null;
    if (actionMessage?.actionDatapoint) {
        datapoint = actionMessage.actionDatapoint;
    } else {
        if (config?.name) {
            datapoint = new ActionDatapoint(config.name, config.options);
        } else if (
            hasQueryStringParameter('autoLogActions') &&
            actionType &&
            actionType.indexOf('=>') == -1
        ) {
            // log action
            datapoint = new ActionDatapoint(actionType, null);
        }

        // only push the datapoint on the stack if it has just been created
        if (datapoint) {
            actionDatapointStack.push(datapoint);
        }
    }

    // if there is any datapoint on the stack then we should log the javascript of all actions taken
    if (actionDatapointStack[0]) {
        actionDatapointStack[0].addCheckmark(actionType + '_js');
    }

    var returnValue = null;
    try {
        if (datapoint && config) {
            if (config.customData) {
                datapoint.addCustomData(config.customData);
            }
            if (config.cosmosOnlyData) {
                datapoint.addCosmosOnlyData(config.cosmosOnlyData);
            }
            if (config.actionSource) {
                datapoint.addActionSource(config.actionSource);
            }
        }

        returnValue = executeNext();
    } catch (error) {
        if (datapoint) {
            const status = getDatapointStatus(error);
            datapoint.endWithError(status, error);

            // if we have a client error, we still need to fix the stack
            actionDatapointStack.pop();
        }

        throw error;
    }

    // if it is a lazy orchestrator, we want the clone of the action to log it,
    // not the initial action that imports the bundle
    if (datapoint && actionMessage && actionMessage.lazyOrchestrator) {
        actionMessage.actionDatapoint = datapoint;
        datapoint = null;
    }

    if (datapoint) {
        // if the return value is not a promise, then let's create a promise and resolve it right away
        // this will capture all of the javascript in the call stack
        var returnPromise = returnValue?.then ? returnValue : Promise.resolve();
        returnPromise
            .then(() => {
                datapoint.addCheckmark('promise');
                safeRequestAnimationFrame(isVisible => {
                    setTimeout(() => {
                        datapoint.endAction(
                            isVisible ? DatapointStatus.Success : DatapointStatus.BackgroundSuccess
                        );
                    });
                });
            })
            .catch((error: Error) => {
                const status = getDatapointStatus(error);
                datapoint.endWithError(status, error);
            });

        actionDatapointStack.pop();
    }

    return returnValue;
}
